import {
    ErrorBoundary,
    ErrorBoundaryProps,
    FallbackRender,
} from '@sentry/react';
import { Stack } from '@mui/material';
import { sentryUnmaskText } from '../../../utils/sentry';
import SuiTypography from '@upheal/design-system/components/legacy/Sui/SuiTypography';
import SuiBox from '@upheal/design-system/components/legacy/Sui/SuiBox';
import SuiButton from '@upheal/design-system/components/legacy/Sui/SuiButton';
import { PAGE_LOAD_TIMESTAMP } from '../../../utils/page-load-utils';
import logger from '@upheal/utils/logger';
import { useMount } from 'react-use';

type Props = {
    sentryExtra?: Record<string, unknown>;
    children: React.ReactNode;
};

const UnexpectedError: FallbackRender = ({ resetError }) => {
    useMount(() => {
        logger.debug(
            '[generic-error-boundary] showing unexpected error fallback',
        );
    });

    return (
        <Stack
            spacing={2}
            maxWidth="100%"
            whiteSpace="normal"
            p={2}
            {...sentryUnmaskText}
        >
            <SuiTypography variant="h3">Unexpected error 😨</SuiTypography>
            <SuiTypography mb={1}>
                We are really sorry, we didn't expect this. The error has been
                automatically reported to us. We will do our best to fix it as
                soon as possible.
            </SuiTypography>
            <SuiBox>
                <SuiButton onClick={resetError}>Try again</SuiButton>
            </SuiBox>
        </Stack>
    );
};

const UnableToLoadPage: FallbackRender = () => {
    useMount(() => {
        logger.debug(
            '[generic-error-boundary] showing unable to load page fallback',
        );
    });

    return (
        <Stack
            spacing={2}
            maxWidth="100%"
            whiteSpace="normal"
            p={2}
            {...sentryUnmaskText}
        >
            <SuiTypography variant="h3">Unable to Load Page</SuiTypography>
            <SuiTypography mb={1}>
                The request timed out. This can happen if you are not connected
                to the internet or your internet connection is unstable. Please
                check your network connection and try again.
            </SuiTypography>
            <SuiBox>
                <SuiButton
                    color="action"
                    onClick={() => {
                        window.location.reload();
                    }}
                >
                    Reload Page
                </SuiButton>
            </SuiBox>
        </Stack>
    );
};

const UpdateAvailable: FallbackRender = () => {
    useMount(() => {
        logger.debug(
            '[generic-error-boundary] showing update available fallback',
        );
    });

    return (
        <Stack
            spacing={2}
            maxWidth="100%"
            whiteSpace="normal"
            p={2}
            {...sentryUnmaskText}
        >
            <SuiTypography variant="h3">Update Available!</SuiTypography>
            <SuiTypography mb={1}>
                Enjoy the latest app version for enhanced features and
                performance. Refresh now to access the exciting new updates.
                Thank you for your continued support!
            </SuiTypography>
            <SuiBox>
                <SuiButton
                    color="action"
                    onClick={() => {
                        window.location.reload();
                    }}
                >
                    Refresh
                </SuiButton>
            </SuiBox>
        </Stack>
    );
};

const AutomaticTranslationError: FallbackRender = ({ resetError }) => {
    useMount(() => {
        logger.debug(
            '[generic-error-boundary] showing automatic translation error',
        );
    });

    return (
        <Stack
            spacing={2}
            maxWidth="100%"
            whiteSpace="normal"
            p={2}
            {...sentryUnmaskText}
        >
            <SuiTypography variant="h3">Translation Error</SuiTypography>
            <SuiTypography mb={1}>
                Upheal doesn't officially support automatic translation (using
                Google Translate or similar) to your language yet and the
                application might behave unexpectedly.
            </SuiTypography>
            <SuiTypography mb={1}>
                Please disable automatic translation in your browser to continue
                using Upheal without issues.
            </SuiTypography>
            <SuiBox>
                <SuiButton color="action" onClick={resetError}>
                    Continue
                </SuiButton>
            </SuiBox>
        </Stack>
    );
};

const CHUNK_LOADING_FAILURE_MESSAGES = [
    'Unable to preload',
    'Failed to fetch dynamically',
    'Importing a module script failed',
];
const isChunkLoadingFailure = (error: Error) =>
    CHUNK_LOADING_FAILURE_MESSAGES.find((msg) => error?.message?.includes(msg));

// https://github.com/facebook/react/issues/11538#issue-273303484
const AUTOMATIC_TRANSLATION_REACT_ERROR_REGEX =
    /Failed to execute '([a-zA-Z]+)' on 'Node'/;
const isAutomaticTranslationError = (error: Error) => {
    try {
        const htmlEl = document.getElementsByTagName('html')[0];
        return (
            error?.message?.match(AUTOMATIC_TRANSLATION_REACT_ERROR_REGEX) &&
            htmlEl?.getAttribute('lang') !== 'en'
        );
    } catch {
        return false;
    }
};

const FallbackComponent: FallbackRender = (props) => {
    const { error } = props;

    const getContentComponent = () => {
        if (error instanceof Error && isAutomaticTranslationError(error)) {
            return AutomaticTranslationError;
        }

        if (error instanceof Error && isChunkLoadingFailure(error)) {
            const timeSincePageLoad = Date.now() - PAGE_LOAD_TIMESTAMP;

            return timeSincePageLoad < 30_000
                ? UnableToLoadPage
                : UpdateAvailable;
        }

        return UnexpectedError;
    };

    const ContentComponent = getContentComponent();

    return (
        <SuiBox
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
        >
            <ContentComponent {...props} />
        </SuiBox>
    );
};

const GenericErrorBoundary = (props: Props) => {
    const { sentryExtra, children } = props;

    const handleBeforeCapture: ErrorBoundaryProps['beforeCapture'] = (
        scope,
    ) => {
        if (sentryExtra) {
            scope.setExtras(sentryExtra);
        }
    };

    return (
        <ErrorBoundary
            fallback={FallbackComponent}
            beforeCapture={handleBeforeCapture}
        >
            {children}
        </ErrorBoundary>
    );
};

export default GenericErrorBoundary;
